import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Arrow } from '../../../icons/SvgIcons';
import { DropdownProps } from '../Dropdown/Dropdown.types';
import { TruncatedText } from '../../../utils/TruncatedText';

function DropdownPortal({
  label,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  disabled = false,
  error,
  className = '',
  size = 'md',
  buttonClassName = 'h-[42px] px-[13px] py-[9px]',
  placeholderClassName,
  menuClassName = '',
  optionClassName = '',
  allOptions,
  searchable = false,
  searchInTrigger = false,
  searchPlaceholder = 'Search',
  noOptionsMessage = 'No options found'
}: Readonly<DropdownProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const inputTriggerRef = useRef<HTMLInputElement>(null);

  const selectedOption =
    options.find((option) => option.value === value) ||
    allOptions?.find((option) => option.value === value);
  const usesInputSearch = searchable && searchInTrigger;

  const filteredOptions = useMemo(() => {
    if (!searchable || searchQuery.trim() === '') {
      return options;
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch)
    );
  }, [options, searchQuery, searchable]);

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  function handleToggle() {
    if (!disabled) {
      if (!isOpen) {
        updateMenuPosition();
      }
      setIsOpen(!isOpen);
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleScroll(event: Event) {
      // Don't close if scrolling within the dropdown menu itself
      if (menuRef.current && menuRef.current.contains(event.target as Node)) {
        return;
      }

      // Close dropdown when scrolling outside of it
      setIsOpen(false);
    }

    function handleResize() {
      // Close dropdown when window is resized
      setIsOpen(false);
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, { capture: true });
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      return;
    }

    if (usesInputSearch) {
      window.setTimeout(() => inputTriggerRef.current?.focus(), 0);
      return;
    }

    if (searchable) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable, usesInputSearch]);

  function handleInputTriggerFocus() {
    if (disabled || isOpen) return;
    setSearchQuery('');
    updateMenuPosition();
    setIsOpen(true);
  }

  function handleInputTriggerChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchQuery(event.target.value);
    if (!isOpen) {
      updateMenuPosition();
      setIsOpen(true);
    }
  }

  function handleOptionClick(optionValue: string) {
    onChange(optionValue);
    setIsOpen(false);
  }

  const inputTriggerValue = isOpen
    ? searchQuery
    : selectedOption
      ? selectedOption.label
      : '';

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${menuPosition.top - window.scrollY}px`,
        left: `${menuPosition.left - window.scrollX}px`,
        width: `${menuPosition.width}px`,
        zIndex: 999999
      }}
      className={`bg-white rounded-md shadow-lg max-h-[180px] overflow-auto border border-gray-200 ${menuClassName}`}
    >
      {searchable && !searchInTrigger && (
        <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={searchPlaceholder}
            className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm leading-5 text-gray-800 placeholder:text-gray-550 outline-none focus:border-purple-700 focus:ring-1 focus:ring-purple-700"
          />
        </div>
      )}
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              handleOptionClick(option.value);
            }}
            className={`max-w-full h-9 px-4 py-2 cursor-pointer font-inter font-normal text-sm leading-5 text-gray-800 hover:bg-gray-100 hover:text-gray-900 flex items-center w-full text-left ${optionClassName}`}
            style={{ gap: '8px' }}
          >
            <TruncatedText text={option.label} />
          </button>
        ))
      ) : (
        <div className="px-4 py-2 font-inter text-sm leading-5 text-gray-550">
          {noOptionsMessage}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`flex flex-col w-full gap-1.5 ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="text-gray-800 text-left font-inter font-medium text-sm leading-5">
          {label}
        </label>
      )}

      <div className="relative">
        {usesInputSearch ? (
          <>
            <input
              ref={(element) => {
                triggerRef.current = element;
                inputTriggerRef.current = element;
              }}
              type="text"
              value={inputTriggerValue}
              onFocus={handleInputTriggerFocus}
              onClick={handleInputTriggerFocus}
              onChange={handleInputTriggerChange}
              onKeyDown={(event) => event.stopPropagation()}
              disabled={disabled}
              placeholder={isOpen ? searchPlaceholder : placeholder}
              title={selectedOption ? selectedOption.label : placeholder}
              className={`w-full rounded-md border font-inter text-left transition-all duration-200 shadow-sm outline-none focus:outline-none pr-10 ${buttonClassName} ${
                size === 'sm'
                  ? 'px-2.5 py-1.5 text-sm font-medium leading-5'
                  : 'h-[42px] px-[13px] py-[9px] text-sm leading-5'
              } ${
                isOpen
                  ? 'border-purple-700 ring-1 ring-purple-700'
                  : 'border-gray-350'
              } ${
                disabled
                  ? 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-550'
                  : 'bg-white cursor-text'
              } ${
                inputTriggerValue ? 'text-gray-800' : 'text-gray-550'
              } ${placeholderClassName} ${error ? 'border-red-300' : ''}`}
            />
            <button
              type="button"
              onClick={handleToggle}
              disabled={disabled}
              className="absolute inset-y-0 right-3 flex items-center"
              aria-label={isOpen ? 'Close options' : 'Open options'}
              tabIndex={-1}
            >
              <div
                className={`transition-transform duration-300 ${
                  isOpen ? 'rotate-180' : 'rotate-0'
                }`}
              >
                {Arrow('down')}
              </div>
            </button>
          </>
        ) : (
          <button
            ref={(element) => {
              triggerRef.current = element;
            }}
            type="button"
            onClick={handleToggle}
            disabled={disabled}
            className={`w-full flex gap-1 items-center justify-between rounded-md border font-inter text-left transition-all duration-200 shadow-sm outline-none focus:outline-none ${buttonClassName} ${
              size === 'sm'
                ? 'px-2.5 py-1.5 text-sm font-medium leading-5'
                : 'h-[42px] px-[13px] py-[9px] text-sm leading-5'
            } ${
              isOpen
                ? 'border-purple-700 ring-1 ring-purple-700'
                : 'border-gray-350'
            } ${
              disabled
                ? 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-550'
                : 'bg-white cursor-pointer'
            } ${error ? 'border-red-300' : ''}`}
          >
            <span
              className={`${
                size === 'sm' ? 'text-sm font-medium leading-5' : 'text-base'
              } ${
                selectedOption ? 'text-gray-800' : 'text-gray-550'
              } ${placeholderClassName} flex-1 min-w-0 truncate`}
              title={selectedOption ? selectedOption.label : placeholder}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>

            <div
              className={`transition-transform duration-300  ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            >
              {Arrow('down')}
            </div>
          </button>
        )}

        {globalThis.window !== undefined &&
          createPortal(dropdownMenu, document.body)}
      </div>

      {error && (
        <span className="text-red-500 font-inter font-normal text-sm">
          {error}
        </span>
      )}
    </div>
  );
}

export default DropdownPortal;
